import React, { useContext, useEffect, useState, useMemo } from 'react'

import styles from './style.module.scss'

import XHeader from 'v2/containers/header'
import XFooter from 'v2/containers/footer'

import { useXTheme } from 'v2/hooks/useV2Theme'

import { UserContext } from 'v2/context/user'
import router from 'next/router'
import { useOrganization } from '../../context/organization'
import ConditionalRender from 'utils/conditionalRender'

import clsx from 'clsx'

import XSideBar from '../sideBar'
import { desktopThreshold } from 'components/BaseComponents/constants'
import XNav from 'v2/components/XNav'

import XAuthMobileFooter from '../xFooterLoggedIn'
import { useTranslation } from 'i18n'
import { useProtectedRoute } from '../../../utils/authentication'

interface Props {
    children?: any
    isLanguageSwitcher?: boolean
    sidebar?: boolean
}

const XAuthPage = (props: Props) => {
    useXTheme()
    useProtectedRoute()
    const { children, isLanguageSwitcher } = props
    const { authenticatedUser } = useContext(UserContext)
    const { t } = useTranslation(window.LANG_NS)
    const isAdmin = window.location.href.includes('admin')
    const userRoutes = useMemo(() => {
        return [
            { label: t('side_bar_button_home'), url: '/' },
            { label: t('side_bar_button_account'), url: '/account' },
            { label: t('side_bar_button_resources'), url: '/resources' },
            { label: t('side_bar_button_help'), url: '/help' },
        ]
    }, [t])

    const [isMobile, setIsMobile] = useState<boolean>(false)

    const onResize = () => {
        setIsMobile(window.innerWidth < desktopThreshold)
    }

    useEffect(() => {
        onResize()
        window.removeEventListener('resize', onResize)
        window.addEventListener('resize', onResize)
    }, [])

    const routes = useMemo(() => {
        if (isAdmin && authenticatedUser?.isSuperuser === 2 && process.env.NEXT_PUBLIC_ADMIN_ENABLED === 'yes') {
            return superAdminRoutes
        } else if (isAdmin && authenticatedUser?.isSuperuser === 1 && process.env.NEXT_PUBLIC_ADMIN_ENABLED === 'yes') {
            return adminRoutes
        }
        return userRoutes
    }, [isAdmin, t])

    return (
        <div
            className={clsx(
                styles.authPage,
                props.sidebar ? styles.authPage__withSideBar : styles.authPage__withBackgroundImage
            )}
            style={{
                overflowY: children ? 'auto' : 'hidden',
            }}
        >
            <ConditionalRender if={!props.sidebar}>
                <XHeader />
                <div className={styles.authPage__content}>{children}</div>
                <XFooter isLanguageSwitcher={isLanguageSwitcher} />
            </ConditionalRender>

            <ConditionalRender if={props.sidebar}>
                <ConditionalRender if={isMobile}>
                    <XHeader />
                    <XNav categories={routes} />
                </ConditionalRender>
                <div className={styles.wrapper}>
                    <ConditionalRender if={!isMobile}>
                        <div className={styles.placeholder}></div>
                        <XSideBar />
                    </ConditionalRender>
                    {children}
                </div>
                <XAuthMobileFooter />
            </ConditionalRender>
        </div>
    )
}

XAuthPage.displayName = 'XAuthPage'

export default XAuthPage

const adminRoutes = [
    { label: 'Home', url: '/admin' },
    { label: 'Applications', url: '/admin/applications' },
    { label: 'Heat Map Signup', url: '/admin/heat-map-signup' },
]

const superAdminRoutes = [
    { label: 'Home', url: '/admin' },
    { label: 'Payments', url: '/admin/resources' },
    { label: 'Text Messages', url: '/admin/text-messages' },
    { label: 'Users', url: '/admin/users' },
    { label: 'Stats', url: '/admin/stats' },
    { label: 'Configurations', url: '/admin/configurations' },
    { label: 'Reset Translation Cache', url: '#' },
    { label: 'Applications', url: '/admin/applications' },
    { label: 'Heat Map Signup', url: '/admin/heat-map-signup' },
]
